import React, { useEffect, useRef } from 'react';
import { Container, Typography, Button, Box, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';
import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { alpha } from '@mui/material/styles';
import * as THREE from 'three';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useMagnetic } from '../hooks/useMagnetic';

gsap.registerPlugin(ScrollTrigger);

// ─── Content ────────────────────────────────────────────────────────────────

const userData = {
  name: 'Syed Saad Waqar',
  title: 'Full-Stack & AI Developer',
  summary:
    'Full-Stack Developer crafting production-grade web apps and AI-powered systems for US-based clients. From Django REST to Node.js/TypeScript, React to LangChain, RAG pipelines to n8n automations I pick up whatever the job needs and ship it. Currently the sole architect of a workflow-driven Staffing CRM at Xeemax Digitals while pursuing BS Computer Science at NED University.',
  resumeLink: '/Resume/Resume - Syed Saad Waqar.pdf',
  github: 'https://github.com/saad9797',
  linkedin: 'https://www.linkedin.com/in/syed-saad-waqar-97167a258/',
  email: 'waqarsaad76@gmail.com',
};

const stats = [
  { value: '1+', label: 'YEAR EXP.' },
  { value: '12+', label: 'PROJECTS' },
  { value: '30+', label: 'TECH STACK' },
  { value: '4', label: 'CLIENTS' },
];

const clientReach = [
  { key: 'national', label: 'clients.national', dot: '#6366f1', names: 'NED University, K-Electric' },
  { key: 'international', label: 'clients.international', dot: '#22d3ee', names: 'Moghal Industries, SMS Group (US)' },
];

const socialLinks = [
  { icon: <GitHubIcon fontSize="small" />, label: 'GitHub', href: userData.github, color: '#f8fafc' },
  { icon: <LinkedInIcon fontSize="small" />, label: 'LinkedIn', href: userData.linkedin, color: '#38bdf8' },
  { icon: <EmailIcon fontSize="small" />, label: 'Email', href: `mailto:${userData.email}`, color: '#818cf8' },
];

const BOOT_LINES = [
  '> booting saad_waqar.dev',
  '> stack: react · node · typescript · prisma · django',
  '> role: full-stack + ai developer',
  '> status: open_to_opportunities [ok]',
];

const TECH_NODES = ['React', 'Node.js', 'TypeScript', 'Prisma', 'PostgreSQL', 'Django', 'FastAPI', 'Azure'];

const MONO = "'JetBrains Mono', 'Fira Code', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

// ─── Three.js signature scene: a live graph of the actual tech stack ───────
// A single confident centerpiece rather than ambient decoration: nodes for
// each technology, connected by edges with light "data pulses" traveling
// along them, parallaxing gently with the cursor. Canvas sits behind the DOM.

function NetworkScene({ dense }) {
  const canvasHostRef = useRef(null);
  const labelHostRefs = useRef([]);

  useEffect(() => {
    const host = canvasHostRef.current;
    if (!host) return undefined;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    } catch (e) {
      return undefined; // no WebGL — background simply stays plain
    }

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.set(0, 0, 11);

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    host.appendChild(renderer.domElement);

    const group = new THREE.Group();
    scene.add(group);

    // ── Build nodes: 8 labeled "tech" nodes + ambient unlabeled nodes ──
    const labeledCount = TECH_NODES.length;
    const ambientCount = dense ? 34 : 18;
    const total = labeledCount + ambientCount;
    const positions = [];

    for (let i = 0; i < labeledCount; i++) {
      const t = i / labeledCount;
      const angle = t * Math.PI * 2;
      const radius = 4.2 + Math.sin(i * 1.7) * 1.1;
      positions.push([
        Math.cos(angle) * radius + 1.5,
        Math.sin(angle) * radius * 0.55,
        (Math.random() - 0.5) * 2,
      ]);
    }
    for (let i = 0; i < ambientCount; i++) {
      positions.push([
        (Math.random() - 0.5) * 13 + 1.5,
        (Math.random() - 0.5) * 7,
        (Math.random() - 0.5) * 4,
      ]);
    }

    // ── Points ──
    const pointGeo = new THREE.BufferGeometry();
    const pointArr = new Float32Array(total * 3);
    positions.forEach(([x, y, z], i) => {
      pointArr[i * 3] = x;
      pointArr[i * 3 + 1] = y;
      pointArr[i * 3 + 2] = z;
    });
    pointGeo.setAttribute('position', new THREE.BufferAttribute(pointArr, 3));
    const pointMat = new THREE.PointsMaterial({
      color: 0x818cf8, size: 0.09, transparent: true, opacity: 0.85, sizeAttenuation: true,
    });
    const points = new THREE.Points(pointGeo, pointMat);
    group.add(points);

    // labeled nodes rendered slightly larger, separate point cloud
    const labelGeo = new THREE.BufferGeometry();
    const labelArr = new Float32Array(labeledCount * 3);
    for (let i = 0; i < labeledCount; i++) {
      labelArr[i * 3] = positions[i][0];
      labelArr[i * 3 + 1] = positions[i][1];
      labelArr[i * 3 + 2] = positions[i][2];
    }
    labelGeo.setAttribute('position', new THREE.BufferAttribute(labelArr, 3));
    const labelMat = new THREE.PointsMaterial({
      color: 0x22d3ee, size: 0.16, transparent: true, opacity: 0.95, sizeAttenuation: true,
    });
    const labelPoints = new THREE.Points(labelGeo, labelMat);
    group.add(labelPoints);

    // ── Edges: connect nodes within a distance threshold ──
    const edgePositions = [];
    const threshold = 5.4;
    for (let i = 0; i < total; i++) {
      for (let j = i + 1; j < total; j++) {
        const [x1, y1, z1] = positions[i];
        const [x2, y2, z2] = positions[j];
        const d = Math.hypot(x1 - x2, y1 - y2, z1 - z2);
        if (d < threshold) {
          edgePositions.push(x1, y1, z1, x2, y2, z2);
        }
      }
    }
    const edgeGeo = new THREE.BufferGeometry();
    edgeGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(edgePositions), 3));
    const edgeMat = new THREE.LineBasicMaterial({ color: 0x4c51bf, transparent: true, opacity: 0.16 });
    const edges = new THREE.LineSegments(edgeGeo, edgeMat);
    group.add(edges);

    // ── Data pulses: small dots traveling along a few edges ──
    const pulseCount = Math.min(6, Math.floor(edgePositions.length / 6));
    const pulses = [];
    for (let i = 0; i < pulseCount; i++) {
      const edgeIndex = Math.floor(Math.random() * (edgePositions.length / 6)) * 6;
      const geo = new THREE.SphereGeometry(0.045, 8, 8);
      const mat = new THREE.MeshBasicMaterial({ color: 0x22d3ee, transparent: true, opacity: 0.9 });
      const mesh = new THREE.Mesh(geo, mat);
      group.add(mesh);
      pulses.push({
        mesh,
        from: new THREE.Vector3(edgePositions[edgeIndex], edgePositions[edgeIndex + 1], edgePositions[edgeIndex + 2]),
        to: new THREE.Vector3(edgePositions[edgeIndex + 3], edgePositions[edgeIndex + 4], edgePositions[edgeIndex + 5]),
        t: Math.random(),
        speed: 0.15 + Math.random() * 0.15,
      });
    }

    // ── Sizing ──
    const resize = () => {
      const w = host.clientWidth, h = host.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    resize();
    window.addEventListener('resize', resize);

    // ── Pointer parallax ──
    const pointer = { x: 0, y: 0 };
    const onPointerMove = (e) => {
      const rect = host.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
    };
    if (!prefersReduced) window.addEventListener('pointermove', onPointerMove);

    // ── Render loop ──
    let raf;
    const clock = new THREE.Clock();
    const tmpVec = new THREE.Vector3();

    const tick = () => {
      const dt = clock.getDelta();
      const elapsed = clock.getElapsedTime();

      if (!prefersReduced) {
        group.rotation.y += dt * 0.035;
        group.rotation.x += (pointer.y * 0.15 - group.rotation.x) * 0.04;
        group.rotation.y += pointer.x * 0.0002;
        pointMat.opacity = 0.7 + Math.sin(elapsed * 0.8) * 0.12;

        pulses.forEach((p) => {
          p.t += dt * p.speed;
          if (p.t > 1) p.t = 0;
          tmpVec.lerpVectors(p.from, p.to, p.t);
          p.mesh.position.copy(tmpVec);
        });
      }

      // project labeled nodes to screen space for HTML overlay
      group.updateMatrixWorld();
      const posAttr = labelGeo.attributes.position;
      for (let i = 0; i < labeledCount; i++) {
        const v = new THREE.Vector3(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
        v.applyMatrix4(group.matrixWorld);
        v.project(camera);
        const el = labelHostRefs.current[i];
        if (el) {
          const x = (v.x * 0.5 + 0.5) * host.clientWidth;
          const y = (-v.y * 0.5 + 0.5) * host.clientHeight;
          const behind = v.z > 1;
          el.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
          el.style.opacity = behind ? '0' : String(0.55 + (1 - v.z) * 0.2);
        }
      }

      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onPointerMove);
      pointGeo.dispose(); pointMat.dispose();
      labelGeo.dispose(); labelMat.dispose();
      edgeGeo.dispose(); edgeMat.dispose();
      pulses.forEach((p) => { p.mesh.geometry.dispose(); p.mesh.material.dispose(); });
      renderer.dispose();
      if (host.contains(renderer.domElement)) host.removeChild(renderer.domElement);
    };
  }, [dense]);

  return (
    <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden' }}>
      <Box ref={canvasHostRef} sx={{ position: 'absolute', inset: 0 }} />
      {TECH_NODES.map((label, i) => (
        <Box
          key={label}
          ref={(el) => { labelHostRefs.current[i] = el; }}
          sx={{
            position: 'absolute', top: 0, left: 0, pointerEvents: 'none',
            fontFamily: MONO, fontSize: '0.66rem', fontWeight: 500,
            color: '#a5b4fc', whiteSpace: 'nowrap', willChange: 'transform',
            textShadow: '0 0 8px rgba(11,15,23,0.9), 0 0 2px rgba(11,15,23,1)',
          }}
        >
          {label}
        </Box>
      ))}
    </Box>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function Home() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const primaryCtaRef = useMagnetic(0.25);

  const rootRef = useRef(null);
  const bootRef = useRef(null);
  const bootLineRefs = useRef([]);
  const heroRef = useRef(null);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const alreadyBooted = sessionStorage.getItem('sw_intro_played');

    const ctx = gsap.context(() => {
      const revealEls = gsap.utils.toArray('.gsap-reveal', rootRef.current);
      const nameMask = rootRef.current.querySelector('.name-mask-inner');
      const titleMask = rootRef.current.querySelector('.title-mask-inner');

      if (prefersReduced || alreadyBooted) {
        // Skip the boot sequence entirely; content is simply present.
        gsap.set(bootRef.current, { display: 'none' });
        gsap.set([nameMask, titleMask], { yPercent: 0 });
        gsap.set(revealEls, { autoAlpha: 1, y: 0 });
      } else {
        gsap.set(revealEls, { autoAlpha: 0, y: 16 });
        gsap.set([nameMask, titleMask], { yPercent: 110 });

        const tl = gsap.timeline({
          onComplete: () => sessionStorage.setItem('sw_intro_played', '1'),
        });

        bootLineRefs.current.forEach((el, i) => {
          if (!el) return;
          tl.fromTo(el, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.05 }, i === 0 ? 0.15 : '+=0.16');
        });

        tl.to(bootRef.current, { autoAlpha: 0, duration: 0.45, delay: 0.35, ease: 'power1.out' })
          .to(nameMask, { yPercent: 0, duration: 0.7, ease: 'power4.out' }, '-=0.15')
          .to(titleMask, { yPercent: 0, duration: 0.6, ease: 'power4.out' }, '-=0.5')
          .to(revealEls, { autoAlpha: 1, y: 0, duration: 0.55, stagger: 0.07, ease: 'power2.out' }, '-=0.35');
      }

      // Subtle scroll-linked fade as the hero leaves the viewport.
      if (!prefersReduced && heroRef.current) {
        gsap.to(heroRef.current, {
          opacity: 0.25,
          y: -30,
          ease: 'none',
          scrollTrigger: { trigger: rootRef.current, start: 'top top', end: 'bottom top', scrub: true },
        });
      }
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <Container
      id="home"
      ref={rootRef}
      maxWidth={false}
      disableGutters
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #0b0f17 0%, #0d1220 100%)',
      }}
    >
      {/* ── Boot sequence overlay ── */}
      <Box
        ref={bootRef}
        sx={{
          position: 'absolute', inset: 0, zIndex: 40,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#0b0f17', px: 3,
        }}
      >
        <Box sx={{ fontFamily: MONO, fontSize: { xs: '0.78rem', md: '0.92rem' }, lineHeight: 2.1 }}>
          {BOOT_LINES.map((line, i) => (
            <Box
              key={i}
              ref={(el) => { bootLineRefs.current[i] = el; }}
              sx={{ opacity: 0, color: i === BOOT_LINES.length - 1 ? '#22d3ee' : '#94a3b8' }}
            >
              {line}
              {i === BOOT_LINES.length - 1 && (
                <Box component="span" sx={{
                  display: 'inline-block', width: '0.55em', height: '1em', ml: 0.75,
                  backgroundColor: '#22d3ee', verticalAlign: 'text-bottom',
                  animation: 'sw-blink 0.9s step-end infinite',
                  '@keyframes sw-blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0 } },
                }} />
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Signature 3D scene ── */}
      <NetworkScene dense={!isMobile} />

      {/* Left-edge vignette so the graph never fights with text legibility */}
      <Box sx={{
        position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
        background: 'linear-gradient(90deg, #0b0f17 0%, rgba(11,15,23,0.75) 32%, rgba(11,15,23,0.15) 60%, transparent 78%)',
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, minHeight: '100vh', display: 'flex', alignItems: 'center', pt: { xs: 9, sm: 11, md: 8 }, pb: { xs: 6, md: 8 } }}>
        <Box ref={heroRef} sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          gap: { xs: 4, sm: 5, md: 7 },
          width: '100%',
        }}>

          {/* ─── Text content ─── */}
          <Box sx={{ flex: '1 1 0', minWidth: 0, textAlign: { xs: 'center', md: 'left' }, order: { xs: 2, md: 1 } }}>

            {/* Status badge */}
            <Box className="gsap-reveal" sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.875,
              px: 1.5, py: 0.5, mb: { xs: 1.75, md: 2.25 }, borderRadius: '999px',
              background: alpha('#10b981', 0.08), border: `1px solid ${alpha('#10b981', 0.22)}`,
            }}>
              <Box sx={{
                width: 6, height: 6, borderRadius: '50%', backgroundColor: '#10b981',
                boxShadow: '0 0 7px rgba(16,185,129,0.8)',
                animation: 'sw-pulse 2.2s ease-in-out infinite',
                '@keyframes sw-pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
              }} />
              <Typography sx={{ color: '#34d399', fontWeight: 700, letterSpacing: '0.07em', fontSize: '0.68rem', fontFamily: MONO }}>
                OPEN_TO_OPPORTUNITIES
              </Typography>
            </Box>

            {/* Name — masked wipe reveal */}
            <Box sx={{ overflow: 'hidden', mb: 0.5 }}>
              <Typography variant="h1" className="name-mask-inner" sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', sm: '2.6rem', md: '3rem', lg: '3.6rem' },
                lineHeight: 1.06, letterSpacing: '-0.02em',
                background: 'linear-gradient(130deg, #f8fafc 20%, #94a3b8 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
              }}>
                {userData.name}
              </Typography>
            </Box>

            {/* Title — masked wipe reveal */}
            <Box sx={{ overflow: 'hidden', mb: { xs: 1.75, md: 2 } }}>
              <Typography className="title-mask-inner" sx={{
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.2rem', md: '1.45rem' },
                lineHeight: 1.3,
                background: 'linear-gradient(90deg, #818cf8 0%, #22d3ee 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
              }}>
                {userData.title}
              </Typography>
            </Box>

            {/* Stats */}
            <Box className="gsap-reveal" sx={{
              display: 'flex', gap: { xs: 2.5, md: 3 }, mb: { xs: 1.75, md: 2.25 },
              flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' },
            }}>
              {stats.map((s, i) => (
                <Box key={i} sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 800, lineHeight: 1.1, color: '#818cf8', fontSize: { xs: '1.25rem', md: '1.4rem' } }}>
                    {s.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: '#8992a6', fontWeight: 600, letterSpacing: '0.05em', fontFamily: MONO }}>
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Summary */}
            <Typography className="gsap-reveal" sx={{
              color: '#a3adc2', fontSize: { xs: '0.875rem', sm: '0.9rem', md: '0.95rem' },
              lineHeight: 1.8, mb: { xs: 2, md: 2.25 },
              maxWidth: { xs: '520px', md: 'none' }, mx: { xs: 'auto', md: 0 },
            }}>
              {userData.summary}
            </Typography>

            {/* Client reach — terminal-styled */}
            <Box className="gsap-reveal" sx={{
              mb: { xs: 2.25, md: 2.5 }, mx: { xs: 'auto', md: 0 },
              maxWidth: { xs: '460px', md: 'none' },
              px: { xs: 1.5, md: 1.75 }, py: 1.25,
              borderRadius: 1.5, border: `1px solid ${alpha('#818cf8', 0.16)}`,
              background: alpha('#818cf8', 0.04),
              textAlign: 'left',
            }}>
              {clientReach.map((c) => (
                <Typography key={c.key} sx={{ fontFamily: MONO, fontSize: '0.74rem', lineHeight: 1.9, color: '#8992a6' }}>
                  <Box component="span" sx={{ color: c.dot }}>$</Box>{' '}
                  <Box component="span" sx={{ color: '#c7cede' }}>{c.label}</Box>
                  {'  →  '}
                  <Box component="span" sx={{ color: '#e2e5ec' }}>{c.names}</Box>
                </Typography>
              ))}
            </Box>

            {/* CTAs */}
            <Box className="gsap-reveal" sx={{
              display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap',
              gap: 1.25, mb: { xs: 2.25, md: 2.5 }, justifyContent: { xs: 'center', md: 'flex-start' },
            }}>
              <Button
                ref={primaryCtaRef}
                data-cursor="hover"
                variant="contained" size="medium"
                href="#projects" endIcon={<ArrowForwardIcon />}
                fullWidth={isMobile}
                sx={{
                  px: { xs: 3, md: 2.75 }, py: { xs: 1.25, md: 1.1 },
                  fontSize: '0.875rem', fontWeight: 600, borderRadius: 2, textTransform: 'none',
                  background: 'linear-gradient(135deg, #6366f1, #4338ca)',
                  boxShadow: '0 4px 14px rgba(99,102,241,0.28)',
                  transition: 'box-shadow 0.25s ease, transform 0.2s ease',
                  '&:hover': { boxShadow: '0 6px 20px rgba(99,102,241,0.4)', transform: 'translateY(-1px)' },
                }}
              >
                View My Work
              </Button>
              <Button
                variant="outlined" size="medium"
                data-cursor="hover"
                href={userData.resumeLink} download
                startIcon={<FileDownloadIcon />}
                fullWidth={isMobile}
                sx={{
                  px: { xs: 3, md: 2.75 }, py: { xs: 1.25, md: 1.1 },
                  fontSize: '0.875rem', fontWeight: 600, borderRadius: 2, textTransform: 'none', borderWidth: '1.5px',
                  borderColor: alpha('#f8fafc', 0.14), color: '#f1f5f9',
                  transition: 'all 0.2s ease',
                  '&:hover': { borderWidth: '1.5px', borderColor: alpha('#6366f1', 0.5), backgroundColor: alpha('#6366f1', 0.08) },
                }}
              >
                Download Resume
              </Button>
            </Box>

            {/* Social links */}
            <Box className="gsap-reveal" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, justifyContent: { xs: 'center', md: 'flex-start' } }}>
              <Typography sx={{ color: '#5b6478', fontSize: '0.75rem', fontWeight: 500, fontFamily: MONO }}>
                connect:
              </Typography>
              {socialLinks.map((link, i) => (
                <Tooltip key={i} title={link.label} arrow>
                  <IconButton
                    href={link.href}
                    data-cursor="hover"
                    target={link.label !== 'Email' ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    size="small"
                    sx={{
                      width: { xs: 40, md: 36 }, height: { xs: 40, md: 36 },
                      border: `1px solid ${alpha('#f8fafc', 0.14)}`, color: '#a3adc2',
                      transition: 'all 0.2s ease',
                      '&:hover': { borderColor: alpha(link.color, 0.4), color: link.color, backgroundColor: alpha(link.color, 0.08), transform: 'translateY(-2px)' },
                    }}
                  >
                    {link.icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Box>

          </Box>

          {/* ─── Profile photo — corner-bracket frame, no rings/glows ─── */}
          <Box className="gsap-reveal" sx={{
            flexShrink: 0,
            width: { xs: '58%', sm: '42%', md: '30%' },
            maxWidth: { xs: 210, sm: 250, md: 'none' },
            position: 'relative',
            order: { xs: 1, md: 2 },
          }}>
            <Box sx={{ position: 'relative', p: 1.25 }}>
              {[
                { top: 0, left: 0, borderTop: '2px solid #6366f1', borderLeft: '2px solid #6366f1' },
                { top: 0, right: 0, borderTop: '2px solid #6366f1', borderRight: '2px solid #6366f1' },
                { bottom: 0, left: 0, borderBottom: '2px solid #22d3ee', borderLeft: '2px solid #22d3ee' },
                { bottom: 0, right: 0, borderBottom: '2px solid #22d3ee', borderRight: '2px solid #22d3ee' },
              ].map((pos, i) => (
                <Box key={i} sx={{ position: 'absolute', width: 22, height: 22, ...pos, opacity: 0.85 }} />
              ))}
              <Box
                component="img"
                src={process.env.PUBLIC_URL + '/Images/profileimage.png'}
                alt="Syed Saad Waqar"
                sx={{ width: '100%', height: 'auto', display: 'block', filter: 'drop-shadow(0 16px 34px rgba(0,0,0,0.45))' }}
              />
            </Box>
            <Typography sx={{
              mt: 1.5, textAlign: 'center', fontFamily: MONO, fontSize: '0.68rem', color: '#5b6478',
            }}>
              currently building: staffing_crm.ts
            </Typography>
          </Box>

        </Box>
      </Container>
    </Container>
  );
}